// Live chat — the pure rules behind the CRM chat dock (no fetch, no DOM), so
// test/live-chat.test.js can run them under plain node. The fetchers and the
// cadence live in liveChat.js, which re-exports all of this.

// Who may take chats. Mirrors LIVE_CHAT_ROLES in squish-crm-backend; the
// backend is the gate, this only decides whether to show the dock at all.
export const LIVE_CHAT_ROLES = ['admin', 'support', 'sales'];
export function canTakeChats(role) {
  return LIVE_CHAT_ROLES.includes(role);
}

// Merge a poll's messages into what is already on screen. Keyed on `seq`, so a
// poll that races a send (and returns the message the send already added)
// never shows it twice, and the order is always the server's.
export function mergeMessages(existing, incoming) {
  if (!Array.isArray(incoming) || incoming.length === 0) return existing;
  const bySeq = new Map((existing || []).map((m) => [m.seq, m]));
  for (const m of incoming) if (m && Number.isFinite(m.seq)) bySeq.set(m.seq, m);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export function lastSeqOf(messages) {
  return (messages || []).reduce((max, m) => Math.max(max, Number(m.seq) || 0), 0);
}

// Waiting sessions in `next` that were not in `prev` — the ones worth a ping.
// The first poll after going available counts everything as new, which is
// right: a shopper already waiting when you sit down is the most urgent one.
export function newlyWaiting(prevIds, nextWaiting) {
  const seen = new Set(prevIds || []);
  return (nextWaiting || []).filter((w) => !seen.has(w.sessionId));
}

// "0:42", "3:05" — how long a shopper has been waiting.
export function formatWait(ms) {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// How a system notice reads on the staff side. The shopper's widget renders
// its own translated copy from the same `kind`.
export function systemLine(m) {
  switch (m?.kind) {
    case 'agent_joined': return `${m.agentName || 'A teammate'} joined the chat`;
    case 'agent_left': return `${m.agentName || 'The teammate'} ended the chat`;
    case 'customer_left': return 'The shopper left the chat';
    default: return '';
  }
}

// Why a chat closed, in words for the thread footer.
export function endedLine(endedBy) {
  switch (endedBy) {
    case 'customer': return 'The shopper ended the chat.';
    case 'agent': return 'You ended the chat.';
    case 'idle': return 'Closed after 30 minutes with no messages.';
    case 'agent_gone': return 'Closed — this tab stopped checking in, so the shopper was told the chat ended.';
    case 'superseded': return 'The shopper started a new chat.';
    case 'timeout':
    case 'no_agents': return 'Nobody picked this up in time — the shopper was offered a callback.';
    default: return 'This chat has ended.';
  }
}

// The chat's Case. Mirrors squish-backend lib/handoff-case.js CURATABLE and
// PRIORITIES; the backend re-validates, this only fills the dropdowns.
export const CASE_CATEGORIES = ['Sales', 'Billing', 'Domains', 'Email', 'Hosting', 'Technical', 'Product', 'General'];
export const CASE_PRIORITIES = ['High', 'Medium', 'Low'];

// Only what the staffer actually changed goes to the server — a stale form
// must not write back values someone else just edited.
export function caseEdits(current, form) {
  if (!current || !form) return {};
  const out = {};
  const subject = String(form.subject ?? '').replace(/\s+/g, ' ').trim();
  if (subject && subject !== (current.subject || '')) out.subject = subject;
  if (form.category && form.category !== current.category) out.category = form.category;
  if (form.priority && form.priority !== current.priority) out.priority = form.priority;
  return out;
}
